// Priority Configuration - UNIFIED APPROACH
// Manages priority levels (Low, Medium-Low, Medium, High, Critical)
// Based on Prisma Priority model schema

import { createTrackingConfig } from '../../components/generic/labels';
import { getDateFormat, tableConfig, uiConfig } from '../../config/appConfig';

export const priorityConfig = {
  entityName: 'Priority',
  
  // Backend API Configuration
  api: {
    baseEndpoint: '/priority',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      // Priority specific endpoints
      activate: (id) => `/${id}/activate`,
      deactivate: (id) => `/${id}/deactivate`,
      reorder: '/reorder'
    },
    lookupEndpoints: {
      // Priorities typically don't have lookup dependencies
      // but you could add priority categories, levels, etc.
    }
  },
  
  // UI Configuration - Using centralized app config
  ui: {
    drawerWidth: '700px', // Larger width for priority details and color picker
    defaultPageSize: tableConfig.defaultPageSize, // From app config: 10
    pageSizeOptions: tableConfig.pageSizeOptions, // From app config: [10, 25, 50, 100]
    defaultTableDensity: tableConfig.defaultDensity, // From app config: 'normal'
    requiredFieldStyle: uiConfig.requiredFieldStyle // From app config: 'badge'
  },
  
  // Service configuration
  service: {
    name: 'priorityService',
    methods: {
      getAll: 'getAllPriorities',
      getById: 'getPriorityById', 
      create: 'createPriority',
      update: 'updatePriority',
      delete: 'deletePriority',
      validate: 'validateData'
    }
  },

  // UNIFIED FIELD CONFIGURATION
  fields: [
    // Basic Information
    { 
      name: 'id', 
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    { 
      name: 'name', 
      label: 'Priority Name', 
      type: 'text', 
      required: true,
      placeholder: 'Enter priority name (e.g., High, Critical)',
      table: { header: 'Priority Name', width: '150px', sortable: true },
      filterable: true,
      validation: { 
        required: true, 
      }
    },
    { 
      name: 'description', 
      label: 'Description', 
      type: 'textarea', 
      required: false,
      placeholder: 'Describe when this priority level should be used',
      table: { header: 'Description', width: '250px', sortable: false },
      filterable: true,
      validation: { 
        required: false, 
      },
      rows: 3,
      helperText: 'Brief description of when to use this priority level'
    },
    { 
      name: 'value', 
      label: 'Priority Value', 
      type: 'number', 
      required: true,
      placeholder: 'Enter numeric value (1-5)',
      table: { header: 'Value', width: '80px', sortable: true },
      filterable: true,
      validation: { 
        required: true,
        integer: true
      },
      helperText: 'Numeric value for sorting (1 = lowest, 5 = highest typically)'
    },
    { 
      name: 'isActive', 
      label: 'Active', 
      type: 'switch', 
      required: false,
      defaultValue: true,
      table: { 
        header: 'Status', 
        width: '100px', 
        sortable: true,
        cellRenderer: 'boolean'
      },
      filterable: true,
      validation: { required: false },
      switchLabel: 'Priority is active and available for use',
      description: 'Inactive priorities cannot be assigned to new records'
    },
    { 
      name: 'record_order', 
      label: 'Display Order', 
      type: 'number', 
      required: false,
      placeholder: 'Enter display order (optional)',
      table: { header: 'Order', width: '80px', sortable: true },
      filterable: false,
      validation: { 
        required: false,
        min: 1,
        max: 999,
        integer: true
      },
      helperText: 'Controls the order in dropdown lists and displays'
    },
    { 
      name: 'color', 
      label: 'Display Color', 
      type: 'color', 
      required: false,
      placeholder: 'Choose a color or enter hex/name',
      table: { 
        header: 'Color', 
        width: '100px', 
        sortable: false,
        cellRenderer: 'color'
      },
      filterable: false,
      validation: { 
        required: false,
        pattern: /^(#[0-9A-Fa-f]{6}|#[0-9A-Fa-f]{3}|[a-zA-Z]+)$/,
        message: 'Please enter a valid color (hex code or color name)'
      },
      helperText: 'Used for visual identification in charts and priority indicators'
    },
    
    // Tracking Information (display only)
    { 
      name: 'createdAt', 
      label: 'Created Date', 
      type: 'date', 
      readonly: true,
      table: { 
        header: 'Created', 
        width: tableConfig.dateColumnWidth, // From app config: '120px'
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table') // From app config: 'dd/MM/yyyy'
      },
      filterable: true,
      filterType: 'dateRange'
    },
    { 
      name: 'updatedAt', 
      label: 'Last Updated', 
      type: 'date', 
      readonly: true,
      table: { 
        header: 'Updated', 
        width: tableConfig.dateColumnWidth, // From app config: '120px'
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table') // From app config: 'dd/MM/yyyy'
      },
      filterable: true,
      filterType: 'dateRange'
    }
  ],

  // Table Configuration - Dynamic getter that generates columns from fields
  get tableColumns() {
    return this.fields
      .filter(field => field.table !== false && !field.hidden)
      .map(field => ({
        key: field.name,
        header: field.table?.header || field.label,
        width: field.table?.width || 'auto',
        sortable: field.table?.sortable !== false,
        displayField: field.table?.displayField || field.name,
        cellRenderer: field.table?.cellRenderer || 'default'
      }));
  },

  // Filter Configuration
  filterableFields: ['name', 'description', 'value', 'isActive', 'createdAt', 'updatedAt'],

  // Filter Groups for Basic/Advanced tabs
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic Filters',
      fields: ['name', 'value', 'isActive']
    },
    {
      name: 'advanced',
      label: 'Advanced Filters',
      fields: ['description', 'record_order', 'color', 'createdAt', 'updatedAt']
    }
  ],

  // Custom Sort Orders
  customSortOrders: {
    isActive: [true, false], // Active priorities first
    value: 'desc', // Higher priority values first by default
    record_order: 'asc' // Ascending order by default
  },

  // Messages Configuration
  messages: {
    loading: 'Loading priorities...',
    noData: 'No priorities found. Click "Add Priority" to create the first priority level.',
    loadError: 'Failed to load priorities',
    saveSuccess: 'Priority saved successfully!',
    saveError: 'Failed to save priority',
    deleteSuccess: 'Priority deleted successfully!',
    deleteError: 'Failed to delete priority',
    deleteConfirm: 'Are you sure you want to delete this priority? This action cannot be undone and may affect CAPA records, risk assessments, and other records that use this priority level.',
    activateSuccess: 'Priority activated successfully!',
    deactivateSuccess: 'Priority deactivated successfully!'
  },

  // Form Layout Configuration
  layout: {
    mode: 'sections',
    sections: [
      {
        id: 'basic',
        title: 'Priority Information',
        icon: '🚨',
        columns: 2,
        fields: [
          { name: 'name' },
          { name: 'value' },
          { name: 'description' },
          { name: 'isActive' },
          { name: 'record_order' },
          { name: 'color' }
        ]
      }
    ]
  },

  // Tracking Section Configuration
  trackingSection: createTrackingConfig('Priority'),

  // Labels Configuration
  labels: {
    // Using predefined labels + custom overrides
    singular: 'Priority',
    plural: 'Priorities',
    addButton: 'Add Priority',
    editButton: 'Edit Priority',
    deleteButton: 'Delete Priority',
    
    // Custom labels for priorities
    pageTitle: 'Priority Management',
    pageSubtitle: 'Manage priority levels for CAPA, risk assessments, and other QMS records',
    searchPlaceholder: 'Search priorities by name or description...',
    
    // Form labels
    basicInfoSection: 'Priority Information',
    
    // Action labels
    activatePriority: 'Activate Priority',
    deactivatePriority: 'Deactivate Priority',
    reorderPriorities: 'Reorder Priorities'
  },

  // Fields to remove during data preparation for submission (used by BaseService)
  fieldsToRemove: [
    // Remove system fields that should not be updated
    'id',
    'createdAt',
    'updatedAt', 
    // Remove all relation fields
    'capas',
    'capaActions',
    'riskAssessments',
    'nonConformityReports',
    'changeRequests'
  ],

  // Export filename for data exports
  exportFileName: 'priorities',

  // Validation function for real-time field validation (dynamically assigned)
  validateField: null // Will be set after validation module loads
};
